import React, { useState, useEffect, useRef } from 'react';
import { fetchSessions, deleteSession } from '../../services/apiService';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import 'bootstrap/dist/css/bootstrap.min.css';

import BASE_URL_API from '../../services/baseUrl';
const BASE_URL = BASE_URL_API.replace(/\/api\/?$/, '');
const PER_PAGE = 20;
const STATUSES = ['active', 'expired', 'completed', 'pending', 'cancelled'];
const PERIODS = [
    { label: 'All Time',   value: '' },
    { label: 'Today',      value: 'today' },
    { label: 'This Week',  value: 'week' },
    { label: 'This Month', value: 'month' },
    { label: 'This Year',  value: 'year' },
];

const statusBadgeClass = (status) => {
    const map = {
        active:    'bg-success',
        completed: 'bg-primary',
        pending:   'bg-warning text-dark',
        expired:   'bg-secondary',
        cancelled: 'bg-danger',
    };
    return map[status?.toLowerCase()] || 'bg-secondary';
};

const formatDate = (d) => d ? new Date(d).toLocaleString() : '—';
const formatAmount = (a) => (a !== null && a !== undefined) ? `$${Number(a).toFixed(2)}` : '—';

const Sessions = () => {
    const token = localStorage.getItem('accessToken');

    const [sessions, setSessions]       = useState([]);
    const [loading, setLoading]         = useState(true);
    const [pagination, setPagination]   = useState({ total: 0, page: 1, pages: 1 });

    // Controlled filter state — these are the *applied* values
    const [appliedSearch, setAppliedSearch]   = useState('');
    const [appliedStatus, setAppliedStatus]   = useState('');
    const [appliedPeriod, setAppliedPeriod]   = useState('');

    // Input state — what user is typing before they hit Search
    const [searchInput, setSearchInput] = useState('');
    const [statusInput, setStatusInput] = useState('');

    // Delete confirm
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleting, setDeleting]         = useState(false);

    // Detail modal
    const [detailSession, setDetailSession] = useState(null);

    // ── Core load function — always uses explicit args, no stale closure ──────
    const loadSessions = async (page = 1, search = '', status = '', period = '') => {
        setLoading(true);
        try {
            const res = await fetchSessions(token, page, PER_PAGE, status, search, period);
            setSessions(res.data.sessions);
            setPagination(res.data.pagination);
        } catch {
            toast.error('Failed to load sessions');
        } finally {
            setLoading(false);
        }
    };

    // Initial load
    useEffect(() => {
        loadSessions(1, '', '', '');
    }, []);

    // ── Search submit ─────────────────────────────────────────────────────────
    const handleSearchSubmit = (e) => {
        e.preventDefault();
        setAppliedSearch(searchInput);
        setAppliedStatus(statusInput);
        loadSessions(1, searchInput, statusInput, appliedPeriod);
    };

    // ── Status change instantly filters ──────────────────────────────────────
    const handleStatusChange = (val) => {
        setStatusInput(val);
        setAppliedStatus(val);
        loadSessions(1, appliedSearch, val, appliedPeriod);
    };

    // ── Period change ─────────────────────────────────────────────────────────
    const handlePeriodChange = (val) => {
        setAppliedPeriod(val);
        loadSessions(1, appliedSearch, appliedStatus, val);
    };

    // ── Clear all filters ─────────────────────────────────────────────────────
    const handleClearFilters = () => {
        setSearchInput('');
        setStatusInput('');
        setAppliedSearch('');
        setAppliedStatus('');
        setAppliedPeriod('');
        loadSessions(1, '', '', '');
    };

    // ── Pagination ────────────────────────────────────────────────────────────
    const goToPage = (page) => {
        loadSessions(page, appliedSearch, appliedStatus, appliedPeriod);
    };

    // ── Delete ────────────────────────────────────────────────────────────────
    const handleDeleteConfirm = async () => {
        if (!deleteTarget) return;
        setDeleting(true);
        try {
            await deleteSession(token, deleteTarget._id || deleteTarget.id);
            toast.success(`Session "${deleteTarget.sessionCode}" deleted`);
            setDeleteTarget(null);
            const newPage = sessions.length === 1 && pagination.page > 1
                ? pagination.page - 1
                : pagination.page;
            loadSessions(newPage, appliedSearch, appliedStatus);
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed to delete session');
        } finally {
            setDeleting(false);
        }
    };

    // Stats from current page
    const expiredCount = sessions.filter(s => s.status?.toLowerCase() === 'expired').length;
    const activeCount  = sessions.filter(s => s.status?.toLowerCase() === 'active').length;
    const totalVolume  = sessions.reduce((sum, s) => sum + (Number(s.amount) || 0), 0);
    const hasFilters   = appliedSearch || appliedStatus || appliedPeriod;

    return (
        <div className="container-fluid py-4 px-3 px-md-4">
            <ToastContainer position="top-right" autoClose={3000} />

            {/* Header */}
            <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-4">
                <div>
                    <h2 className="fw-bold mb-0">Sessions</h2>
                    <p className="text-muted small mb-0">{pagination.total} total sessions</p>
                </div>
                <div className="btn-group" role="group">
                    {PERIODS.map(p => (
                        <button
                            key={p.value}
                            type="button"
                            className={`btn btn-sm ${appliedPeriod === p.value ? 'btn-primary' : 'btn-outline-secondary'}`}
                            onClick={() => handlePeriodChange(p.value)}
                        >
                            {p.label}
                        </button>
                    ))}
                </div>
            </div>

            {/* Stats Cards */}
            <div className="row g-3 mb-4">
                <div className="col-6 col-md-3">
                    <div className="card border-0 shadow-sm h-100">
                        <div className="card-body">
                            <p className="text-muted small mb-1 text-uppercase fw-semibold">Total</p>
                            <h3 className="fw-bold text-primary mb-0">{pagination.total}</h3>
                        </div>
                    </div>
                </div>
                <div className="col-6 col-md-3">
                    <div className="card border-0 shadow-sm h-100">
                        <div className="card-body">
                            <p className="text-muted small mb-1 text-uppercase fw-semibold">Active</p>
                            <h3 className="fw-bold text-success mb-0">{activeCount}</h3>
                        </div>
                    </div>
                </div>
                <div className="col-6 col-md-3">
                    <div className="card border-0 shadow-sm h-100">
                        <div className="card-body">
                            <p className="text-muted small mb-1 text-uppercase fw-semibold">Expired</p>
                            <h3 className="fw-bold text-secondary mb-0">{expiredCount}</h3>
                        </div>
                    </div>
                </div>
                <div className="col-6 col-md-3">
                    <div className="card border-0 shadow-sm h-100">
                        <div className="card-body">
                            <p className="text-muted small mb-1 text-uppercase fw-semibold">Page Volume</p>
                            <h3 className="fw-bold text-info mb-0">{formatAmount(totalVolume)}</h3>
                        </div>
                    </div>
                </div>
            </div>

            {/* Filters */}
            <div className="card border-0 shadow-sm mb-4">
                <div className="card-body">
                    <form onSubmit={handleSearchSubmit}>
                        <div className="row g-2 align-items-end">
                            <div className="col-12 col-sm-5 col-md-4">
                                <label className="form-label small fw-semibold text-muted text-uppercase mb-1">
                                    Search Player
                                </label>
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="Search by username..."
                                    value={searchInput}
                                    onChange={(e) => setSearchInput(e.target.value)}
                                />
                            </div>
                            <div className="col-12 col-sm-4 col-md-3">
                                <label className="form-label small fw-semibold text-muted text-uppercase mb-1">
                                    Status
                                </label>
                                <select
                                    className="form-select"
                                    value={statusInput}
                                    onChange={(e) => handleStatusChange(e.target.value)}
                                >
                                    <option value="">All Statuses</option>
                                    {STATUSES.map(s => (
                                        <option key={s} value={s}>
                                            {s.charAt(0).toUpperCase() + s.slice(1)}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div className="col-12 col-sm-auto d-flex gap-2">
                                <button type="submit" className="btn btn-primary">
                                    Search
                                </button>
                                {hasFilters && (
                                    <button
                                        type="button"
                                        className="btn btn-outline-secondary"
                                        onClick={handleClearFilters}
                                    >
                                        Clear
                                    </button>
                                )}
                            </div>
                        </div>
                        {/* Active filter pills */}
                        {hasFilters && (
                            <div className="d-flex flex-wrap gap-2 mt-2">
                                {appliedSearch && (
                                    <span className="badge bg-primary bg-opacity-10 text-primary border border-primary-subtle px-2 py-1">
                                        Player: {appliedSearch}
                                    </span>
                                )}
                                {appliedStatus && (
                                    <span className="badge bg-secondary bg-opacity-10 text-secondary border border-secondary-subtle px-2 py-1">
                                        Status: {appliedStatus}
                                    </span>
                                )}
                            </div>
                        )}
                    </form>
                </div>
            </div>

            {/* Table Card */}
            <div className="card border-0 shadow-sm">
                <div className="card-body p-0">
                    {loading ? (
                        <div className="d-flex justify-content-center align-items-center py-5">
                            <div className="spinner-border text-primary me-2" role="status"></div>
                            <span className="text-muted">Loading sessions...</span>
                        </div>
                    ) : sessions.length === 0 ? (
                        <div className="text-center py-5">
                            <div className="fs-1 mb-2">🕹️</div>
                            <p className="text-muted fw-semibold mb-1">No sessions found</p>
                            <p className="text-muted small mb-3">
                                {hasFilters ? 'No results match your filters.' : 'Sessions will appear here once activity begins.'}
                            </p>
                            {hasFilters && (
                                <button className="btn btn-sm btn-outline-secondary" onClick={handleClearFilters}>
                                    Clear Filters
                                </button>
                            )}
                        </div>
                    ) : (
                        <div className="table-responsive">
                            <table className="table table-hover align-middle mb-0">
                                <thead className="table-light">
                                    <tr>
                                        <th className="ps-3">#</th>
                                        <th>Session</th>
                                        <th className="d-none d-sm-table-cell">Player</th>
                                        <th className="d-none d-md-table-cell">Game</th>
                                        <th className="d-none d-md-table-cell">Room</th>
                                        <th>Amount</th>
                                        <th>Status</th>
                                        <th className="d-none d-lg-table-cell">Expires At</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {sessions.map((session, i) => (
                                        <tr key={session._id || session.id}>
                                            <td className="ps-3 text-muted small">
                                                {(pagination.page - 1) * PER_PAGE + i + 1}
                                            </td>
                                            <td>
                                                <div className="fw-semibold small">{session.sessionCode}</div>
                                                <div className="text-muted" style={{ fontSize: '0.72rem' }}>
                                                    {session.ipAddress}
                                                </div>
                                            </td>
                                            <td className="d-none d-sm-table-cell fw-semibold small">
                                                {session.playerUsername || '—'}
                                            </td>
                                            <td className="d-none d-md-table-cell">
                                                <div className="d-flex align-items-center gap-2">
                                                    {session.game?.thumbnail && (
                                                        <img
                                                            src={`${BASE_URL}${session.game.thumbnail}`}
                                                            alt={session.game.name}
                                                            className="rounded"
                                                            style={{ width: 32, height: 32, objectFit: 'cover' }}
                                                        />
                                                    )}
                                                    <span className="small">{session.game?.name || '—'}</span>
                                                </div>
                                            </td>
                                            <td className="d-none d-md-table-cell small text-muted">
                                                {session.room?.name || '—'}
                                            </td>
                                            <td className="fw-semibold">
                                                {formatAmount(session.amount)}
                                                <span className="text-muted small ms-1">{session.currency}</span>
                                            </td>
                                            <td>
                                                <span className={`badge ${statusBadgeClass(session.status)}`}>
                                                    {session.status}
                                                </span>
                                            </td>
                                            <td className="d-none d-lg-table-cell text-muted small">
                                                {formatDate(session.expiresAt)}
                                            </td>
                                            <td>
                                                <div className="d-flex gap-2">
                                                    <button
                                                        className="btn btn-sm btn-outline-primary"
                                                        onClick={() => setDetailSession(session)}
                                                    >
                                                        View
                                                    </button>
                                                    <button
                                                        className="btn btn-sm btn-outline-danger"
                                                        onClick={() => setDeleteTarget(session)}
                                                    >
                                                        Delete
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {/* Pagination */}
                    {!loading && sessions.length > 0 && (
                        <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 px-3 py-3 border-top">
                            <span className="text-muted small">
                                Page {pagination.page} of {pagination.pages} · {pagination.total} total
                            </span>
                            <nav>
                                <ul className="pagination pagination-sm mb-0">
                                    <li className={`page-item ${pagination.page <= 1 ? 'disabled' : ''}`}>
                                        <button className="page-link" onClick={() => goToPage(pagination.page - 1)}>
                                            &laquo; Prev
                                        </button>
                                    </li>
                                    {Array.from({ length: pagination.pages }, (_, i) => i + 1)
                                        .filter(p => Math.abs(p - pagination.page) <= 2)
                                        .map(p => (
                                            <li key={p} className={`page-item ${p === pagination.page ? 'active' : ''}`}>
                                                <button className="page-link" onClick={() => goToPage(p)}>{p}</button>
                                            </li>
                                        ))
                                    }
                                    <li className={`page-item ${pagination.page >= pagination.pages ? 'disabled' : ''}`}>
                                        <button className="page-link" onClick={() => goToPage(pagination.page + 1)}>
                                            Next &raquo;
                                        </button>
                                    </li>
                                </ul>
                            </nav>
                        </div>
                    )}
                </div>
            </div>

            {/* Detail Modal */}
            {detailSession && (
                <>
                    <div className="modal fade show d-block" tabIndex="-1" role="dialog">
                        <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
                            <div className="modal-content">
                                <div className="modal-header">
                                    <h5 className="modal-title fw-bold">
                                        Session — <span className="text-primary">{detailSession.sessionCode}</span>
                                    </h5>
                                    <button className="btn-close" onClick={() => setDetailSession(null)}></button>
                                </div>
                                <div className="modal-body">
                                    <div className="row g-3">
                                        {/* Highlight */}
                                        <div className="col-12">
                                            <div className="card border-0" style={{ backgroundColor: '#101828' }}>
                                                <div className="card-body d-flex flex-wrap gap-4 align-items-center justify-content-between">
                                                    <div>
                                                        <p className="text-muted small text-uppercase fw-semibold mb-1">Amount</p>
                                                        <h3 className="fw-bold text-primary mb-0">
                                                            {formatAmount(detailSession.amount)}
                                                            <span className="fs-6 text-muted fw-normal ms-1">{detailSession.currency}</span>
                                                        </h3>
                                                    </div>
                                                    <div>
                                                        <p className="text-muted small text-uppercase fw-semibold mb-1">Status</p>
                                                        <span className={`badge fs-6 ${statusBadgeClass(detailSession.status)}`}>
                                                            {detailSession.status}
                                                        </span>
                                                    </div>
                                                    <div>
                                                        <p className="text-muted small text-uppercase fw-semibold mb-1">Session Code</p>
                                                        <span className="badge border fw-bold fs-6" style={{ backgroundColor: '#0B1120', color: '#fff' }}>
                                                            {detailSession.sessionCode}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        {/* Game & Room */}
                                        <div className="col-12"><hr className="my-1" /><p className="fw-bold mb-2 mt-2">Game & Room</p></div>
                                        <div className="col-md-6">
                                            <p className="text-muted small text-uppercase fw-semibold mb-1">Game</p>
                                            <div className="d-flex align-items-center gap-2">
                                                {detailSession.game?.thumbnail && (
                                                    <img src={`${BASE_URL}${detailSession.game.thumbnail}`} alt={detailSession.game.name} className="rounded" style={{ width: 40, height: 40, objectFit: 'cover' }} />
                                                )}
                                                <span className="fw-semibold">{detailSession.game?.name || '—'}</span>
                                            </div>
                                        </div>
                                        <div className="col-md-6">
                                            <p className="text-muted small text-uppercase fw-semibold mb-1">Room</p>
                                            <p className="fw-semibold mb-0">{detailSession.room?.name || '—'}</p>
                                        </div>
                                        {/* Session Details */}
                                        <div className="col-12"><hr className="my-1" /><p className="fw-bold mb-2 mt-2">Session Details</p></div>
                                        <div className="col-md-6">
                                            <p className="text-muted small text-uppercase fw-semibold mb-1">Player</p>
                                            <p className="fw-semibold mb-0">{detailSession.playerUsername || '—'}</p>
                                        </div>
                                        <div className="col-md-6">
                                            <p className="text-muted small text-uppercase fw-semibold mb-1">IP Address</p>
                                            <p className="fw-semibold mb-0">{detailSession.ipAddress || '—'}</p>
                                        </div>
                                        <div className="col-md-6">
                                            <p className="text-muted small text-uppercase fw-semibold mb-1">Invoice ID</p>
                                            <p className="fw-semibold mb-0 text-break">{detailSession.invoiceId || '—'}</p>
                                        </div>
                                        <div className="col-md-6">
                                            <p className="text-muted small text-uppercase fw-semibold mb-1">Session ID</p>
                                            <p className="fw-semibold mb-0 text-break small">{detailSession._id}</p>
                                        </div>
                                        {/* Timestamps */}
                                        <div className="col-12"><hr className="my-1" /><p className="fw-bold mb-2 mt-2">Timestamps</p></div>
                                        <div className="col-md-6">
                                            <p className="text-muted small text-uppercase fw-semibold mb-1">Created At</p>
                                            <p className="fw-semibold mb-0">{formatDate(detailSession.createdAt)}</p>
                                        </div>
                                        <div className="col-md-6">
                                            <p className="text-muted small text-uppercase fw-semibold mb-1">Expires At</p>
                                            <p className="fw-semibold mb-0">{formatDate(detailSession.expiresAt)}</p>
                                        </div>
                                        <div className="col-md-6">
                                            <p className="text-muted small text-uppercase fw-semibold mb-1">Activated At</p>
                                            <p className="fw-semibold mb-0">{formatDate(detailSession.activatedAt)}</p>
                                        </div>
                                        <div className="col-md-6">
                                            <p className="text-muted small text-uppercase fw-semibold mb-1">Completed At</p>
                                            <p className="fw-semibold mb-0">{formatDate(detailSession.completedAt)}</p>
                                        </div>
                                    </div>
                                </div>
                                <div className="modal-footer">
                                    <button className="btn btn-secondary" onClick={() => setDetailSession(null)}>Close</button>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="modal-backdrop fade show"></div>
                </>
            )}

            {/* Delete Confirm Modal */}
            {deleteTarget && (
                <>
                    <div className="modal fade show d-block" tabIndex="-1" role="dialog">
                        <div className="modal-dialog modal-dialog-centered">
                            <div className="modal-content">
                                <div className="modal-header border-0 pb-0">
                                    <h5 className="modal-title fw-bold text-danger">Delete Session</h5>
                                    <button className="btn-close" onClick={() => setDeleteTarget(null)} disabled={deleting}></button>
                                </div>
                                <div className="modal-body pt-2">
                                    <div className="d-flex align-items-center gap-3 mb-3">
                                        {deleteTarget.game?.thumbnail && (
                                            <img src={`${BASE_URL}${deleteTarget.game.thumbnail}`} alt={deleteTarget.game.name} className="rounded" style={{ width: 48, height: 48, objectFit: 'cover' }} />
                                        )}
                                        <div>
                                            <div className="fw-semibold">{deleteTarget.sessionCode}</div>
                                            <div className="text-muted small">{deleteTarget.playerUsername} · {deleteTarget.game?.name}</div>
                                        </div>
                                    </div>
                                    <p className="text-muted mb-0">
                                        Are you sure you want to delete this session? This action <strong>cannot be undone</strong>.
                                    </p>
                                </div>
                                <div className="modal-footer border-0 pt-0">
                                    <button className="btn btn-secondary" onClick={() => setDeleteTarget(null)} disabled={deleting}>Cancel</button>
                                    <button className="btn btn-danger" onClick={handleDeleteConfirm} disabled={deleting}>
                                        {deleting ? (<><span className="spinner-border spinner-border-sm me-2"></span>Deleting...</>) : 'Yes, Delete'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="modal-backdrop fade show"></div>
                </>
            )}
        </div>
    );
};

export default Sessions;